import React, { useState, useEffect } from "react";
import { ArrowUpIcon, TrendingUpIcon, BarChart3Icon, LineChartIcon } from "lucide-react";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../../shadcn/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../../shadcn/ui/select";
import { DatePicker } from "../../../../shadcn/ui/date-time-picker";
import { cn } from "../utils";
import { useUserCurrency } from "../../../../hooks/useUserCurrency";
import billingService from "../../../../services/billingService";
import { getUser } from "../../../../services/localService";
import logger from "../../../../utils/logger";


const TotalRevenueCard = ({ className }) => {
  const [activeTab, setActiveTab] = useState("Chart");
  const [selectedTimeRange, setSelectedTimeRange] = useState("Last 30 Days");
  const [customStartDate, setCustomStartDate] = useState(new Date(new Date().setDate(new Date().getDate() - 30)));
  const [customEndDate, setCustomEndDate] = useState(new Date());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const userCurrency = useUserCurrency();

  const getDateRange = timeRange => {
    const today = new Date();
    const endDate = new Date(today);
    let startDate = new Date(today);

    if (timeRange === "Custom Range") {
      return {
        startDate: customStartDate.toISOString().split("T")[0],
        endDate: customEndDate.toISOString().split("T")[0],
      };
    }

    switch (timeRange) {
      case "Last 7 Days":
        startDate.setDate(today.getDate() - 7);
        break;
      case "Last 30 Days":
        startDate.setDate(today.getDate() - 30);
        break;
      case "Last 90 Days":
        startDate.setDate(today.getDate() - 90);
        break;
      case "This Month":
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        break;
      case "Last Month":
        startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        endDate.setDate(0);
        break;
      default:
        startDate.setDate(today.getDate() - 30);
    }

    return {
      startDate: startDate.toISOString().split("T")[0],
      endDate: endDate.toISOString().split("T")[0],
    };
  };

  const fetchRevenueAggregates = async () => {
    try {
      setLoading(true);
      const user = getUser();
      const organizationId = user?.organizationId || user?.organization?.id || user?.user?.organizationId;

      if (!organizationId) {
        logger.warn("No organization ID found for revenue aggregates");
        setLoading(false);
        return;
      }

      const { startDate, endDate } = getDateRange(selectedTimeRange);
      const period = selectedTimeRange.includes("Month")
        ? "MONTH"
        : selectedTimeRange.includes("Week")
          ? "WEEK"
          : "DAY";

      const response = await billingService.getOrganizationRevenueAggregates(organizationId, {
        startDate,
        endDate,
        period,
      });

      if (response.data?.data) {
        const aggregates = response.data.data;
        const totalAmount = aggregates.reduce((sum, item) => sum + parseFloat(item.total || 0), 0);

        const weeklyData = aggregates.map((item, index) => {
          const date = new Date(item.periodStart);
          const weekLabel =
            period === "MONTH"
              ? date.toLocaleDateString("en-US", { month: "short", day: "numeric" })
              : period === "WEEK"
                ? `Week ${index + 1}`
                : date.toLocaleDateString("en-US", { month: "short", day: "numeric" });

          return {
            week: weekLabel,
            amount: parseFloat(item.total || 0),
          };
        });

        const midPoint = Math.floor(aggregates.length / 2);
        const firstHalf = aggregates.slice(0, midPoint).reduce((sum, item) => sum + parseFloat(item.total || 0), 0);
        const secondHalf = aggregates.slice(midPoint).reduce((sum, item) => sum + parseFloat(item.total || 0), 0);
        const change = firstHalf > 0 ? ((secondHalf - firstHalf) / firstHalf) * 100 : 0;

        setData({
          totalAmount: totalAmount,
          revenueChange: `${change >= 0 ? "+" : ""}${change.toFixed(1)}%`,
          isRevenuePositive: change >= 0,
          weeklyData,
        });
      }
    } catch (error) {
      logger.error("Error fetching revenue aggregates:", error);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRevenueAggregates();
  }, [selectedTimeRange, customStartDate, customEndDate]);

  const handleTimeRangeChange = timeRange => {
    setSelectedTimeRange(timeRange);
  };

  const { totalAmount, revenueChange = "+10%", isRevenuePositive = true, weeklyData = [] } = data || {};

  if (loading) {
    return (
      <Card className={cn("border shadow-none", className)}>
        <CardHeader>
          <div className="animate-pulse">
            <div className="h-6 bg-slate-200 rounded w-32 mb-2"></div>
            <div className="h-4 bg-slate-200 rounded w-24 mb-4"></div>
            <div className="h-10 bg-slate-200 rounded w-24"></div>
          </div>
        </CardHeader>
        <CardContent className="h-[280px] flex items-center justify-center">
          <div className="text-sm text-slate-500">Loading revenue data...</div>
        </CardContent>
      </Card>
    );
  }

  // Calculate heights and y-axis labels dynamically
  const maxAmount = Math.max(...weeklyData.map(item => item.amount), 0);
  const chartHeight = 160; // Reduced height for bars
  const maxBarWidth = 40; // Maximum width for bars

  // Calculate heights for bars with better scaling - start from 0
  const barsWithHeights = weeklyData.map(item => ({
    ...item,
    heightPercent: maxAmount > 0 ? Math.max((item.amount / maxAmount) * 100, 1) : 0, // Minimum 1% for visibility
  }));

  // Generate y-axis labels starting from 0
  const yAxisSteps = 5;
  const yAxisLabels = [];
  for (let i = 0; i < yAxisSteps; i++) {
    const value = (maxAmount / (yAxisSteps - 1)) * (yAxisSteps - 1 - i);
    yAxisLabels.push(
      value >= 1000
        ? `${userCurrency}${(value / 1000).toFixed(1)}k`
        : `${userCurrency}${Math.round(value).toLocaleString()}`
    );
  }

  return (
    <Card className={cn("border shadow-none", className)}>
      <CardHeader>
        <div className="flex justify-between items-start">
          <div>
            <CardTitle className="text-lg font-semibold">Total Revenue</CardTitle>
            <CardDescription>Weekly revenue breakdown</CardDescription>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <p className="text-2xl font-bold">
                {totalAmount !== undefined && totalAmount !== null
                  ? `${userCurrency}${totalAmount.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}`
                  : "—"}
              </p>
              <p
                className={`text-sm flex items-center justify-end ${isRevenuePositive ? "text-green-500" : "text-red-500"}`}
              >
                <ArrowUpIcon className="mr-1 w-4 h-4" />
                {revenueChange}
              </p>
            </div>
            {selectedTimeRange === "Custom Range" && (
              <div className="flex items-center gap-2 animate-in fade-in slide-in-from-right-4 duration-300">
                <DatePicker
                  date={customStartDate}
                  setDate={setCustomStartDate}
                  placeholder="Start Date"
                  className="w-[130px]"
                />
                <span className="text-gray-400 text-sm">to</span>
                <DatePicker
                  date={customEndDate}
                  setDate={setCustomEndDate}
                  placeholder="End Date"
                  className="w-[130px]"
                />
              </div>
            )}
            <Select value={selectedTimeRange} onValueChange={handleTimeRangeChange}>
              <SelectTrigger className="w-[150px] bg-white border-gray-200 h-9">
                <SelectValue placeholder="Select range" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Last 7 Days">Last 7 Days</SelectItem>
                <SelectItem value="Last 30 Days">Last 30 Days</SelectItem>
                <SelectItem value="Last 90 Days">Last 90 Days</SelectItem>
                <SelectItem value="This Month">This Month</SelectItem>
                <SelectItem value="Last Month">Last Month</SelectItem>
                <SelectItem value="Custom Range">Custom Range</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="mt-4">
          <div className="inline-flex p-1 rounded-lg bg-slate-100">
            <button
              onClick={() => setActiveTab("Chart")}
              className={`px-3 py-1 text-sm rounded-md transition-all flex items-center gap-1 ${activeTab === "Chart" ? "bg-white shadow-sm" : "text-slate-600"}`}
            >
              <BarChart3Icon className="h-3 w-3" />
              Chart
            </button>
            <button
              onClick={() => setActiveTab("Trend")}
              className={`px-3 py-1 text-sm rounded-md transition-all flex items-center gap-1 ${activeTab === "Trend" ? "bg-white shadow-sm" : "text-slate-600"}`}
            >
              <LineChartIcon className="h-3 w-3" />
              Trend
            </button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="h-[280px]">
        {activeTab === "Chart" && (
          <div className="h-full flex overflow-hidden">
            {weeklyData.length > 0 ? (
              <>
                <div className="text-xs text-slate-500 flex flex-col justify-between h-full pt-4 pb-[2.5rem] pr-3 border-r border-dashed min-w-[50px]">
                  {yAxisLabels.map((label, index) => (
                    <span key={index} className="text-right leading-none transform translate-y-1/2">
                      {label}
                    </span>
                  ))}
                </div>
                <div className="flex-1 overflow-x-auto overflow-y-hidden">
                  <div
                    className="flex items-end px-4 gap-2 h-full pt-4 pb-2"
                    style={{ minWidth: `${weeklyData.length * 45}px` }}
                  >
                    {barsWithHeights.map((item, index) => (
                      <div
                        key={item.week}
                        className="flex flex-col items-center group h-full justify-end"
                        style={{ width: "40px", minWidth: "40px" }}
                      >
                        <div className="w-full flex-1 flex items-end justify-center mb-2">
                          <div
                            className={cn(
                              "w-full bg-gradient-to-t from-teal-500 to-teal-400 rounded-t transition-all duration-300 hover:from-teal-600 hover:to-teal-500 cursor-pointer relative",
                              index === barsWithHeights.length - 1 && "ring-2 ring-blue-500 ring-offset-1"
                            )}
                            style={{ height: `${item.heightPercent}%`, minHeight: "2px" }}
                          >
                            <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-slate-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                              {userCurrency}
                              {item.amount.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </div>
                          </div>
                        </div>
                        <span className="text-xs text-slate-500 truncate w-full text-center h-6">{item.week}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <div className="text-center text-slate-500">
                  <BarChart3Icon className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No revenue data available</p>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === "Trend" && (
          <div className="h-full overflow-hidden">
            {weeklyData.length > 0 ? (
              <div className="space-y-4">
                {/* Revenue trend visualization */}
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-slate-700 flex items-center gap-1">
                    <TrendingUpIcon className="h-4 w-4" />
                    Revenue Trend
                  </h4>
                  <div className="relative h-32 overflow-hidden">
                    {/* Draw trend line */}
                    <svg className="w-full h-full" viewBox="0 0 400 120" preserveAspectRatio="none">
                      <defs>
                        <linearGradient id="revenueGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                          <stop offset="0%" stopColor="#14b8a6" stopOpacity="0.3" />
                          <stop offset="100%" stopColor="#14b8a6" stopOpacity="0.1" />
                        </linearGradient>
                      </defs>

                      {/* Area under the line */}
                      <path
                        d={`M 0 120 ${weeklyData
                          .map((item, index) => {
                            const x = (index / (weeklyData.length - 1)) * 400;
                            const y = 120 - (item.amount / maxAmount) * 100;
                            return `${index === 0 ? "L" : ""} ${x} ${y}`;
                          })
                          .join(" L ")} L 400 120 Z`}
                        fill="url(#revenueGradient)"
                      />

                      {/* Trend line */}
                      <path
                        d={`M 0 ${120 - (weeklyData[0]?.amount / maxAmount) * 100} ${weeklyData
                          .map((item, index) => {
                            const x = (index / (weeklyData.length - 1)) * 400;
                            const y = 120 - (item.amount / maxAmount) * 100;
                            return `L ${x} ${y}`;
                          })
                          .join(" ")}`}
                        fill="none"
                        stroke="#14b8a6"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />

                      {/* Data points */}
                      {weeklyData.map((item, index) => {
                        const x = (index / (weeklyData.length - 1)) * 400;
                        const y = 120 - (item.amount / maxAmount) * 100;
                        return (
                          <circle
                            key={index}
                            cx={x}
                            cy={y}
                            r="4"
                            fill="#14b8a6"
                            className="hover:r-6 transition-all duration-200"
                            title={`${userCurrency}${item.amount.toLocaleString()}`}
                          />
                        );
                      })}
                    </svg>

                    {/* Period labels */}
                    <div className="flex justify-between mt-2 px-2 overflow-x-auto">
                      {weeklyData.map((item, index) => (
                        <span key={index} className="text-xs text-slate-500 flex-shrink-0">
                          {item.week}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Summary stats */}
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="p-2 bg-slate-50 rounded">
                    <p className="text-lg font-bold text-teal-600">
                      {userCurrency}
                      {weeklyData.reduce((sum, item) => sum + item.amount, 0).toLocaleString()}
                    </p>
                    <p className="text-xs text-slate-500">Total Period</p>
                  </div>
                  <div className="p-2 bg-slate-50 rounded">
                    <p className="text-lg font-bold text-blue-600">
                      {userCurrency}
                      {(weeklyData.reduce((sum, item) => sum + item.amount, 0) / weeklyData.length).toLocaleString(
                        undefined,
                        { maximumFractionDigits: 0 }
                      )}
                    </p>
                    <p className="text-xs text-slate-500">Avg/Period</p>
                  </div>
                  <div className="p-2 bg-slate-50 rounded">
                    <p className="text-lg font-bold text-green-600">{revenueChange}</p>
                    <p className="text-xs text-slate-500">Change</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <div className="text-center text-slate-500">
                  <TrendingUpIcon className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No trend data available</p>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TotalRevenueCard;
